import React from "react";
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NavigateBefore, NavigateNext, Send, Edit } from "@mui/icons-material";
import { PreviewTemplate } from "../../../components/Preview/PreviewTemplate";
import { LoadState } from "../../../constants/enums";
import { formatText } from "../../../helpers";
import WhatsappBgImg from "../../../assets/whatsapp_bg_img.jpg";
import { StatusRenderer } from "../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { IQuickReplyTemplateData } from "../../../redux/quickReplyTemplates/quickReplyTemplate.types";

interface IQuickReplyTemplateCardsV2Props {
  templates: IQuickReplyTemplateData[];
  selectedTemplate: string | null;
  onWhatsappMessageSend: (template: IQuickReplyTemplateData) => void;
  toNumber?: string;
  currentPage?: number;
  totalRecords?: number;
  onPageChange: (
    direction?: "next" | "prev",
    cursor?: string,
    rowsPerPage?: number,
  ) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPage: number;
  searchTemplateName?: string;
  onTemplatePreview: (template: IQuickReplyTemplateData) => void;
  onTemplateDetails: (template: IQuickReplyTemplateData) => void;
  isFromQuickReply?: boolean;
  isFromChat?: boolean;
  compactView?: boolean;
  showCreatePanel?: boolean;
  onEditClick?: (template: IQuickReplyTemplateData) => void;
}

export const QuickReplyTemplateCardsV2: React.FC<
  IQuickReplyTemplateCardsV2Props
> = ({
  templates,
  selectedTemplate,
  onWhatsappMessageSend,
  toNumber,
  currentPage = 1,
  totalRecords = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPage,
  searchTemplateName,
  onTemplatePreview,
  onTemplateDetails,
  isFromQuickReply = false,
  isFromChat = false,
  compactView = false,
  showCreatePanel = false,
  onEditClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const filteredTemplates = templates.filter((template) => {
    if (!searchTemplateName) return true;
    const searchTerms = searchTemplateName.toLowerCase().trim().split(/\s+/);
    const templateName = template.name.toLowerCase().trim();
    return searchTerms.every((term) => templateName.includes(term));
  });

  // New traditional pagination logic
  const isBackDisabled = currentPage <= 1;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const isNextDisabled =
    currentPage >= totalPages || templates.length < rowsPerPage;

  const [hoveredTemplate, setHoveredTemplate] = React.useState<string | null>(
    null,
  );

  return (
    <Box mt={isMobile ? 1 : 2}>
      <Grid
        container
        spacing={isMobile ? 1 : 2}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: compactView ? "flex-start" : "center",
        }}
      >
        {filteredTemplates.map((template) => {
          const isApproved = isFromQuickReply
            ? template.status === "ACTIVE"
            : template.status === "APPROVED";
          return (
            <Grid
              item
              xs={12}
              sm={showCreatePanel ? 12 : compactView ? 6 : 6}
              md={showCreatePanel ? 12 : compactView ? 6 : 6}
              lg={showCreatePanel ? 12 : compactView ? 6 : 4}
              key={template.name}
              sx={{ mb: compactView ? (isMobile ? 1 : 2) : 4 }}
            >
              <Card
                onClick={(e) => {
                  if (isApproved) {
                    onTemplateDetails(template);
                  }
                }}
                onMouseEnter={() => setHoveredTemplate(template.name)}
                onMouseLeave={() => setHoveredTemplate(null)}
                sx={{
                  height: "100%",
                  minHeight: compactView
                    ? isMobile
                      ? "170px"
                      : "200px"
                    : isMobile
                      ? "350px"
                      : "400px",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  borderRadius: 2,
                  boxShadow: 1,
                  cursor: isApproved ? "pointer" : "not-allowed",
                  backgroundColor: "#efeae2",
                  backgroundImage: `url(${WhatsappBgImg})`,
                  backgroundRepeat: "repeat",
                  backgroundSize: "auto",
                  backgroundPosition: "center",
                  backgroundBlendMode: "multiply",
                }}
              >
                {/* {isFromChat && hoveredTemplate === template.name && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 18,
                      right: 55,
                      zIndex: 1,
                    }}
                  >
                    <StatusRenderer status={template?.status || ""} />
                  </Box>
                )} */}

                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 10,
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  {!isFromChat && (
                    <StatusRenderer status={template?.status || ""} />
                  )}
                  {isFromChat && (
                    <Tooltip title="Send WhatsApp Message">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isApproved && toNumber) {
                            onWhatsappMessageSend(template);
                          }
                        }}
                        disabled={!isApproved || !toNumber}
                        sx={{
                          rotate: "-30deg",
                          color:
                            isApproved && toNumber
                              ? "success.main"
                              : "action.disabled",
                        }}
                        size="small"
                      >
                        <Send fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {onEditClick && !template.master_quick_reply_template_uuid && (
                    <Tooltip title="Edit Template">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditClick(template);
                        }}
                        sx={{
                          color: "primary.main",
                          backgroundColor: "rgba(255, 255, 255, 0.85)",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                          },
                          boxShadow: "0px 1px 3px rgba(0,0,0,0.2)",
                        }}
                        size="small"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <CardContent
                  sx={{
                    flexGrow: 1,
                    cursor: isApproved ? "pointer" : "not-allowed",
                  }}
                  onClick={() => {
                    if (isApproved) {
                      onTemplatePreview(template);
                    }
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: "bold",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      mt: -1,
                    }}
                  >
                    {formatText(template.name)}
                  </Typography>

                  <Box
                    sx={{
                      height: "100%",
                      mb: 2,
                      display: "flex",
                      alignItems: "flex-end",
                    }}
                  >
                    <PreviewTemplate
                      template={template as IQuickReplyTemplateData}
                      loading={LoadState.InProgress}
                      isFromQuickReply={true}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box
        display="flex"
        justifyContent={isMobile ? "center" : "flex-end"}
        alignItems="center"
        p={isMobile ? 0.5 : 2}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={isMobile ? 0.5 : 1}
          sx={{
            flexWrap: isMobile ? "wrap" : "nowrap",
            justifyContent: isMobile ? "center" : "flex-end",
            rowGap: isMobile ? 0.5 : 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {isMobile ? "Rows:" : "Templates per page:"}
          </Typography>
          <FormControl size="small">
            <Select
              value={rowsPerPage}
              onChange={(e) => {
                const newRowsPerPage = e.target.value as number;
                onRowsPerPageChange(newRowsPerPage);
                onPageChange(undefined, undefined, newRowsPerPage);
              }}
              sx={{ color: "text.secondary", minWidth: isMobile ? 64 : 74 }}
            >
              <MenuItem value={9}>9</MenuItem>
              <MenuItem value={18}>18</MenuItem>
              <MenuItem value={36}>36</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={() => onPageChange("prev")}
            disabled={isBackDisabled}
            size={isMobile ? "small" : "medium"}
          >
            <NavigateBefore />
            {!isMobile && <Typography variant="body2">Back</Typography>}
          </IconButton>

          <IconButton
            onClick={() => onPageChange("next")}
            disabled={isNextDisabled}
            size={isMobile ? "small" : "medium"}
          >
            {!isMobile && <Typography variant="body2">Next</Typography>}
            <NavigateNext />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
};
